import { Heading } from '@slal/ui/typography';
import React from 'react';
import { DescriptionList, DocumentLink, Notice } from '~/components';
import {
    useApplicationContext,
    useApplicationInvestmentPathwayFundCode,
    useApplicationPensions,
    useFundSelector,
    useSelectedInvestmentPathway,
} from '~/hooks';
import { formatDate, formatToGBP } from '~/utils';
import { EditPersonalInformationFormModal } from '../PersonalInformationForm';
import { ReviewEditWarningModal } from './ReviewEditWarningModal';
import {
    MultilineValue,
    ReviewActions,
    ReviewCard,
    ReviewWithdrawalContainer,
    ReviewWithdrawalWrapper,
    StyledDescriptionList,
    StyledFundWrapper,
    StyledHr,
    StyledReviewContainer,
} from './styles';
import { ReviewFundProps } from './types';
import { formatDateOrdinal, getWithdrawalLabelValue } from './utils';

export const ReviewPensionDetails = () => {
    const pensions = useApplicationPensions();

    const id = 'review-application-pension-transfer';

    return (
        <StyledReviewContainer>
            <Heading level={3} id={id}>
                ReAssure pensions to transfer
            </Heading>
            <ReviewCard aria-labelledby={id}>
                {pensions?.map(({ policyNumber, value }, index) => (
                    <React.Fragment key={index}>
                        <StyledDescriptionList
                            className="no-hr"
                            items={[
                                {
                                    label: 'Policy number',
                                    value: policyNumber,
                                },
                                {
                                    label: 'Current value',
                                    value: formatToGBP(value),
                                },
                            ]}
                        />
                        {index < pensions.length - 1 && <StyledHr />}
                    </React.Fragment>
                ))}
                <ReviewActions>
                    <ReviewEditWarningModal title="Changing your pension details" pathname="/application/pensions" />
                </ReviewActions>
            </ReviewCard>
        </StyledReviewContainer>
    );
};

export const ReviewPersonalDetails = () => {
    const { personalInformation } = useApplicationContext();

    const ContactDetails = () => (
        <>
            <MultilineValue>{personalInformation?.emailAddress}</MultilineValue>
            <MultilineValue>{personalInformation?.phoneNumber}</MultilineValue>
        </>
    );

    const AddressDetails = () => (
        <>
            <MultilineValue>{personalInformation?.addressLineOne}</MultilineValue>
            <MultilineValue>{personalInformation?.addressLineTwo}</MultilineValue>
            <MultilineValue>{personalInformation?.addressLineThree}</MultilineValue>
            <MultilineValue>{personalInformation?.postcode}</MultilineValue>
        </>
    );

    const details = [
        {
            label: 'Name',
            value: `${personalInformation?.title} ${personalInformation?.firstName} ${personalInformation?.lastName}`,
        },
        { label: 'Date of birth', value: formatDate(personalInformation?.dateOfBirth) },
        {
            label: 'Contact details',
            value: <ContactDetails />,
        },
        {
            label: 'Address',
            value: <AddressDetails />,
        },
        { label: 'National Insurance number', value: personalInformation?.nationalInsuranceNumber },
        { label: 'Employment status', value: personalInformation?.employmentStatus },
        { label: 'Marital status', value: personalInformation?.maritalStatus },
    ];

    const id = 'review-application-personal-information';

    return (
        <StyledReviewContainer>
            <Heading level={3} id={id}>
                Your details
            </Heading>
            <ReviewCard aria-labelledby={id}>
                <StyledDescriptionList items={details} />
                <ReviewActions>
                    <EditPersonalInformationFormModal />
                </ReviewActions>
            </ReviewCard>
        </StyledReviewContainer>
    );
};

export const ReviewInvestmentPathwayOption = () => {
    const fundCode = useApplicationInvestmentPathwayFundCode();
    const { investmentPathway } = useSelectedInvestmentPathway(fundCode || undefined);
    if (!fundCode) return null;
    if (!investmentPathway) return null;

    const labelId = 'review-invesment-pathway';

    const { fundName, factsheetUrl } = investmentPathway;

    return (
        <StyledReviewContainer>
            <Heading level={3} id={labelId}>
                Ready made investment
            </Heading>
            <ReviewCard aria-labelledby={labelId}>
                <Heading level={4}>{fundName}</Heading>
                {factsheetUrl && <DocumentLink href={factsheetUrl}>Fund factsheet</DocumentLink>}
                <ReviewActions>
                    <ReviewEditWarningModal
                        title="Changing your investment options"
                        pathname="/application/investment-options"
                    />
                </ReviewActions>
            </ReviewCard>
        </StyledReviewContainer>
    );
};

export const ReviewFundChoice = ({ factsheetUrl, fundCode, fundName, allocation }: ReviewFundProps) => (
    <StyledFundWrapper key={fundCode}>
        <DescriptionList
            aria-label={fundName}
            items={[
                {
                    label: 'Fund name',
                    value: fundName,
                },
                {
                    label: 'Fund percentage',
                    value: `${isNaN(parseFloat(allocation)) ? 0 : allocation}%`,
                },
            ]}
        />

        {factsheetUrl && <DocumentLink href={factsheetUrl}>Fund factsheet</DocumentLink>}
    </StyledFundWrapper>
);

export const ReviewFundOptions = () => {
    const { fundAllocations } = useFundSelector();
    const id = 'review-fund-options';
    return (
        <StyledReviewContainer>
            <Heading level={3} id={id}>
                Your investments
            </Heading>
            <ReviewCard aria-labelledby={id} role="list">
                {fundAllocations &&
                    fundAllocations.map(fundAllocation => (
                        <ReviewFundChoice role="listitem" key={fundAllocation.fundCode} {...fundAllocation} />
                    ))}

                <ReviewActions>
                    <ReviewEditWarningModal
                        title="Changing your investment options"
                        pathname="/application/investment-options"
                    />
                </ReviewActions>
            </ReviewCard>
        </StyledReviewContainer>
    );
};

export const ReviewInvestmentOption = () => {
    const { investmentOption } = useApplicationContext();
    return investmentOption === 'custom' ? <ReviewFundOptions /> : <ReviewInvestmentPathwayOption />;
};

export const ReviewLumpSum = () => {
    const { pensionValues } = useApplicationContext();

    const { label, value } = getWithdrawalLabelValue(pensionValues);

    return (
        <StyledReviewContainer>
            <ReviewCard aria-label="Your lump sum">
                <StyledDescriptionList
                    items={[{ label: 'Lump sum amount', value: `${label}${formatToGBP(value!)}` }]}
                />
                <Notice>
                    If the value of your pension rises or falls before the day of payment, the amount you receive may
                    change.
                </Notice>
                <ReviewActions>
                    <ReviewEditWarningModal title="Changing your lump sum" pathname="/application/lump-sum-options" />
                </ReviewActions>
            </ReviewCard>
        </StyledReviewContainer>
    );
};

export const ReviewRegularIncome = () => {
    const {
        pensionValues: { withdrawals },
    } = useApplicationContext();
    const paymentDate = formatDateOrdinal(withdrawals?.regularIncomePaymentDay);

    return (
        <StyledReviewContainer>
            <ReviewCard aria-labelledby="review-application-regular-income">
                <StyledDescriptionList
                    items={[
                        {
                            label: 'Monthly income',
                            value: withdrawals?.regularIncome ? formatToGBP(withdrawals?.regularIncome) : '-',
                        },
                        { label: 'Monthly income payment date', value: paymentDate },
                    ]}
                />
                <ReviewActions>
                    <ReviewEditWarningModal
                        title="Changing your monthly income"
                        pathname="/application/regular-income"
                    />
                </ReviewActions>
            </ReviewCard>
        </StyledReviewContainer>
    );
};

export const ReviewWithdrawal = () => {
    const { withdrawalOption } = useApplicationContext();

    const id = 'review-withdrawal';

    return (
        <ReviewWithdrawalContainer>
            <Heading level={3} id={id}>
                Your withdrawal
            </Heading>

            {withdrawalOption === 'Lump sum' && <ReviewLumpSum />}
            {withdrawalOption === 'Regular income' && <ReviewRegularIncome />}
            {withdrawalOption === 'Both' && (
                <ReviewWithdrawalWrapper aria-labelledby={id}>
                    <ReviewLumpSum />
                    <ReviewRegularIncome />
                </ReviewWithdrawalWrapper>
            )}
        </ReviewWithdrawalContainer>
    );
};
